import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, MessageCircle, MessageCircleQuestion, Minus, Plus, ShoppingCart, Star, Truck, ShieldCheck, Zap, Volume2, Square, Bell, Users } from "lucide-react";
import api from "../lib/api";
import { fmtTRY, effectivePrice, discountPercent  } from "../lib/format";
import { errText } from "../lib/errors";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/ProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [qa, setQa] = useState([]);
  const [qaForm, setQaForm] = useState({ name: "", email: "", question: "" });
  const [qaDone, setQaDone] = useState(false);
  const [tab, setTab] = useState("desc");
  const goQA = () => { setTab("qa"); setTimeout(() => document.getElementById("product-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); };
  const [settings, setSettings] = useState(null);
  const [img, setImg] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [grams, setGrams] = useState(250);
  const [rv, setRv] = useState({ rating: 5, comment: "" });
  const [speaking, setSpeaking] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertDone, setAlertDone] = useState(false);
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertForm, setAlertForm] = useState({ email: "", phone: "" });
  const [alsoBought, setAlsoBought] = useState([]);

  const submitAlert = async () => {
    if (!alertForm.email.trim() && !alertForm.phone.trim()) { toast.error("E-posta veya telefon girin"); return; }
    setAlertBusy(true);
    try {
      const { data } = await api.post(`/products/${p.product_id}/stock-alert`, alertForm);
      toast.success(data.message);
      setAlertDone(true);
    } catch (err) { toast.error(errText(err) || "Kaydedilemedi"); }
    finally { setAlertBusy(false); }
  };

  const cleanForSpeech = (t) =>
    (t || "")
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // markdown link → sadece metni
      .replace(/^\s*[-–—+]\s+/gm, "") // satır başı madde işaretleri
      .replace(/[*_#>`~|•●○◆►▪▫★☆✓✔]+/g, " ") // markdown işaretleri ve madde simgeleri
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{2190}-\u{21FF}]/gu, " ") // emoji ve ok simgeleri
      .replace(/[ \t]*\n[ \t]*/g, ". ") // satır sonları cümle arasına
      .replace(/\s+/g, " ")
      .replace(/(\.\s*){2,}/g, ". ")
      .trim();

  const readAloud = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    if (!p || !("speechSynthesis" in window)) return;
    const desc = cleanForSpeech(p.description);
    if (!desc) { toast.info("Bu üründe okunacak açıklama yok"); return; }
    const parts = [desc];
    const u = new SpeechSynthesisUtterance(parts.join(" "));
    u.lang = "tr-TR";
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  useEffect(() => {
    setP(null); setImg(0); setVariant(null); setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false));
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!p) return;
    api.get(`/products/${p.product_id}/related`).then(({ data }) => setRelated(data)).catch(() => {});
    api.get(`/products/${p.product_id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.get(`/products/${p.product_id}/questions`).then(({ data }) => setQa(data)).catch(() => {});
    api.get(`/products/${p.product_id}/also-bought`).then(({ data }) => setAlsoBought(data)).catch(() => {});
    trackEvent("view_item", { currency: "TRY", value: p.discount_price ?? p.price, items: [{ item_id: p.product_id, item_name: p.name, price: p.discount_price ?? p.price }] });
  }, [p]);

  if (p === null) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-500">Yükleniyor...</div>;
  if (p === false) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><p className="font-serif text-2xl text-stone-700">Ürün bulunamadı</p><Link to="/urunler" className="text-forest font-semibold mt-4 inline-block">Ürünlere dön</Link></div>;

  const price = variant ? (variant.discount_price ?? variant.price) : effectivePrice(p);
  const pct = variant ? (variant.discount_price ? Math.round((1 - variant.discount_price / variant.price) * 100) : 0) : discountPercent(p);
  const submitQuestion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${p.product_id}/questions`, { ...qaForm, name: qaForm.name || user?.name || "", email: qaForm.email || user?.email || "" });
      setQaDone(true);
      setQaForm({ name: "", email: "", question: "" });
      toast.success("Sorunuz alındı — yanıtlanınca bu sayfada yayınlanacak");
    } catch (err) { toast.error(errText(err) || "Gönderilemedi"); }
  };

  const waMsg = `Merhaba, ${p.name} (SKU: ${p.sku}) hakkında bilgi almak istiyorum.`;
  const waLink = settings?.whatsapp ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(waMsg)}` : "#";

  const addToCart = () => {
    if (p.sale_mode === "acik") addItem(p, grams / 1000, { name: `${grams} gr`, price: p.price, discount_price: p.discount_price });
    else addItem(p, qty, variant);
  };
  const buyNow = () => { addToCart(); navigate("/odeme"); };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/products/${p.product_id}/reviews`, rv);
      toast.success(data.message);
      setRv({ rating: 5, comment: "" });
    } catch (err) { toast.error(errText(err) || "Yorum gönderilemedi"); }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.images,
    description: p.short_description, sku: p.sku, brand: { "@type": "Brand", name: p.brand },
    offers: { "@type": "Offer", priceCurrency: "TRY", price: price, availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    ...(p.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } } : {}),
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <nav className="text-sm text-stone-500 mb-6">
        <Link to="/" className="hover:text-forest">Ana Sayfa</Link> / <Link to={`/urunler?kategori=${p.category?.slug}`} className="hover:text-forest">{p.category?.name}</Link> / <span className="text-stone-800">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="rounded-3xl overflow-hidden bg-cream-dark border border-stone-200/70 aspect-square group">
            <img src={p.images[img] || p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-zoom-in" />
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((src, i) => (
                <button key={i} onClick={() => setImg(i)} data-testid={`gallery-thumb-${i}`} className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === img ? "border-forest" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold-dark font-semibold">{p.brand}</p>
          <h1 data-testid="product-title" className="font-serif text-3xl sm:text-4xl text-stone-900 mt-2 leading-tight">{p.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= Math.round(p.rating || 0) ? "fill-gold text-gold" : "text-stone-300"}`} />)}</div>
            <span className="text-sm text-stone-500">{p.rating || 0} ({p.review_count || 0} yorum)</span>
            <span className="text-stone-300">|</span>
            <span className="text-sm text-stone-500">SKU: {p.sku}</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <p data-testid="product-price" className="text-3xl sm:text-4xl font-bold text-forest">{fmtTRY(price)}</p>
            {pct > 0 && <p className="text-lg text-stone-400 line-through">{fmtTRY(variant ? variant.price : p.price)}</p>}
            {pct > 0 && <span className="bg-terra text-white text-xs font-bold rounded-full px-3 py-1">%{pct} indirim</span>}
          </div>
          <p className="text-xs text-stone-500 mt-1">KDV dahil · {p.sale_mode === "acik" ? "kg fiyatı" : p.unit} · {p.weight}</p>

          <p data-testid="stock-status" className={`mt-3 text-sm font-semibold ${p.stock > 0 ? "text-green-700" : "text-terra"}`}>
            {p.stock > 0 ? (p.stock <= p.min_stock ? `Son ${p.stock} adet!` : "Stokta var") : "Stokta yok"}
          </p>
          {p.origin && <p className="text-sm text-stone-600 mt-1">Menşei: <b>{p.origin}</b></p>}

          {p.variants?.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-stone-800 mb-2">Gramaj / Varyant</p>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => (
                  <button key={v.name} data-testid={`variant-${v.name}`} onClick={() => setVariant(v)} className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${variant?.name === v.name ? "border-forest bg-forest text-white" : "border-stone-300 bg-white hover:border-forest"}`}>{v.name} · {fmtTRY(v.discount_price ?? v.price)}</button>
                ))}
              </div>
            </div>
          )}

          {p.stock > 0 ? (
            <>
              <div className="flex items-center gap-3 mt-7">
                {p.sale_mode === "acik" ? (
                  <div data-testid="gram-options" className="flex items-center gap-1 border border-stone-300 rounded-xl bg-white p-1 flex-wrap">
                    {(() => {
                      const std = [100, 250, 500, 1000].map((g) => ({ g, label: g === 1000 ? "1 kg" : `${g} gr` }));
                      const custom = (p.sale_options || []).filter((o) => o.base_qty).map((o) => ({ g: Math.round(o.base_qty * 1000), label: o.label }));
                      const merged = [...std, ...custom.filter((o) => !std.some((s) => s.g === o.g))].sort((a, b) => a.g - b.g);
                      return merged.map((o) => (
                        <button key={o.g} data-testid={`gram-${o.g}`} onClick={() => setGrams(o.g)} className={`px-2.5 py-2 rounded-lg text-xs font-bold transition-colors ${grams === o.g ? "bg-forest text-white" : "text-stone-600 hover:bg-cream-dark"}`}>{o.label}</button>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className="flex items-center border border-stone-300 rounded-xl bg-white">
                    <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-cream-dark rounded-l-xl transition-colors" aria-label="Azalt"><Minus className="w-4 h-4" /></button>
                    <span data-testid="qty-value" className="w-10 text-center font-semibold">{qty}</span>
                    <button data-testid="qty-increase" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="p-3 hover:bg-cream-dark rounded-r-xl transition-colors" aria-label="Artır"><Plus className="w-4 h-4" /></button>
                  </div>
                )}
                <button data-testid="add-to-cart-button" onClick={addToCart} className="flex-1 bg-forest hover:bg-forest-dark text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm">
                  <ShoppingCart className="w-5 h-5" /> Sepete Ekle
                </button>
                <button data-testid="favorite-toggle" onClick={async () => { if (!user) return toast.info("Giriş yapmalısınız"); await api.post(`/favorites/${p.product_id}`); toast.success("Favorilere eklendi"); }} className="p-3.5 rounded-xl border border-stone-300 bg-white hover:border-terra hover:text-terra transition-colors" aria-label="Favori">
                  <Heart className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-3 mt-3">
                <button data-testid="buy-now-button" onClick={buyNow} className="flex-1 bg-gold hover:bg-gold-dark text-stone-900 font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                  <Zap className="w-5 h-5" /> Hemen Satın Al
                </button>
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="product-whatsapp-button" onClick={() => trackEvent("whatsapp_click", { location: "urun_detay" })} className="flex-1 bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> WhatsApp'tan Sor
                </a>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mt-7">
                <button data-testid="stock-alert-open" onClick={() => { setAlertForm((f) => ({ ...f, email: f.email || user?.email || "" })); setAlertOpen((o) => !o); }} className="flex-1 bg-gold hover:bg-gold-dark text-stone-900 font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm">
                  <Bell className="w-5 h-5" /> Stok Gelince Haber Ver
                </button>
                <button data-testid="favorite-toggle" onClick={async () => { if (!user) return toast.info("Giriş yapmalısınız"); await api.post(`/favorites/${p.product_id}`); toast.success("Favorilere eklendi"); }} className="p-3.5 rounded-xl border border-stone-300 bg-white hover:border-terra hover:text-terra transition-colors" aria-label="Favori">
                  <Heart className="w-5 h-5" />
                </button>
              </div>
              {alertOpen && !alertDone && (
                <div data-testid="stock-alert-form" className="mt-3 bg-cream-dark/60 border border-stone-200 rounded-xl p-4 space-y-2.5">
                  <p className="text-sm font-semibold text-stone-800">Ürün stoğa girince size haber verelim</p>
                  <input data-testid="stock-alert-email" type="email" className="w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="E-posta adresiniz" value={alertForm.email} onChange={(e) => setAlertForm({ ...alertForm, email: e.target.value })} />
                  <input data-testid="stock-alert-phone" className="w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="Telefon (opsiyonel — WhatsApp bildirimi için)" value={alertForm.phone} onChange={(e) => setAlertForm({ ...alertForm, phone: e.target.value })} />
                  <button data-testid="stock-alert-submit" onClick={submitAlert} disabled={alertBusy} className="w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold py-3 rounded-xl transition-colors">{alertBusy ? "Kaydediliyor..." : "Bildirim İste"}</button>
                </div>
              )}
              {alertDone && <p data-testid="stock-alert-success" className="mt-3 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">Kaydınız alındı — ürün stoğa girince haber vereceğiz.</p>}
              <div className="flex gap-3 mt-3">
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="product-whatsapp-button" onClick={() => trackEvent("whatsapp_click", { location: "urun_detay" })} className="flex-1 bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> WhatsApp'tan Sor
                </a>
              </div>
            </>
          )}

          <button data-testid="ask-question-button" onClick={goQA} className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-forest text-forest hover:bg-forest hover:text-white rounded-xl py-3 text-sm font-semibold transition-colors">
            <MessageCircleQuestion className="w-5 h-5" /> Soru Sor {qa.length > 0 ? `(${qa.length} yanıtlanmış soru)` : ""}
          </button>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="flex items-center gap-2.5 bg-cream-dark/60 rounded-xl p-3.5"><Truck className="w-5 h-5 text-forest" /><span className="text-xs text-stone-700 font-medium">1-3 iş günü kargo</span></div>
            <div className="flex items-center gap-2.5 bg-cream-dark/60 rounded-xl p-3.5"><ShieldCheck className="w-5 h-5 text-forest" /><span className="text-xs text-stone-700 font-medium">Güvenli ödeme & iade garantisi</span></div>
          </div>
          <button data-testid="read-aloud-button" onClick={readAloud} aria-pressed={speaking} aria-label={speaking ? "Okumayı durdur" : "Ürün özelliklerini sesli oku"} className={`mt-3 w-full flex items-center justify-center gap-2 border rounded-xl py-3 text-sm font-semibold transition-colors ${speaking ? "border-terra text-terra bg-red-50" : "border-stone-300 text-stone-700 hover:border-forest hover:text-forest"}`}>
            {speaking ? <><Square className="w-4 h-4" /> Okumayı Durdur</> : <><Volume2 className="w-4 h-4" /> Ürün Özelliklerini Sesli Oku</>}
          </button>
        </div>
      </div>

      {p.video && (
        <section data-testid="product-video-section" className="mt-10">
          <h2 className="font-serif text-2xl text-forest mb-4">Ürün Videosu</h2>
          <div className="rounded-3xl overflow-hidden border border-stone-200/80 bg-black aspect-video max-w-3xl">
            {/youtube\.com|youtu\.be/.test(p.video) ? (
              <iframe
                src={`https://www.youtube.com/embed/${(p.video.match(/(?:v=|youtu\.be\/)([\w-]{6,})/) || [])[1] || ""}`}
                title={`${p.name} video`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={p.video} controls className="w-full h-full" preload="metadata" />
            )}
          </div>
        </section>
      )}

      <Tabs id="product-tabs" value={tab} onValueChange={setTab} className="mt-14 scroll-mt-28">
        <TabsList className="bg-cream-dark/70 flex-wrap h-auto gap-1 p-1.5 rounded-xl">
          <TabsTrigger data-testid="tab-description" value="desc" className="rounded-lg">Ürün Açıklaması</TabsTrigger>
          <TabsTrigger data-testid="tab-ingredients" value="ing" className="rounded-lg">İçindekiler</TabsTrigger>
          <TabsTrigger data-testid="tab-storage" value="storage" className="rounded-lg">Saklama & Kargo</TabsTrigger>
          <TabsTrigger data-testid="tab-reviews" value="reviews" className="rounded-lg">Yorumlar ({p.review_count || 0})</TabsTrigger>
          <TabsTrigger data-testid="tab-qa" value="qa" className="rounded-lg">Soru & Cevap ({qa.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="desc" className="bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8 mt-4">
          <p className="text-stone-700 leading-relaxed whitespace-pre-line">{p.description}</p>
        </TabsContent>
        <TabsContent value="ing" className="bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8 mt-4 space-y-4">
          <div><h4 className="font-semibold text-stone-900">İçindekiler</h4><p className="text-stone-600 text-sm mt-1">{p.ingredients || "Belirtilmemiş"}</p></div>
          <div><h4 className="font-semibold text-stone-900">Alerjen Bilgisi</h4><p className="text-stone-600 text-sm mt-1">{p.allergens || "Belirtilmemiş"}</p></div>
        </TabsContent>
        <TabsContent value="storage" className="bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8 mt-4 space-y-4">
          <div><h4 className="font-semibold text-stone-900">Saklama Koşulları</h4><p className="text-stone-600 text-sm mt-1">{p.storage || "Serin ve kuru ortamda saklayınız."}</p></div>
          <div><h4 className="font-semibold text-stone-900">Kargo</h4><p className="text-stone-600 text-sm mt-1">Siparişleriniz kırılmaya karşı özel koruyucu ambalajla 1-3 iş günü içinde teslim edilir.</p></div>
          <div><h4 className="font-semibold text-stone-900">İade</h4><p className="text-stone-600 text-sm mt-1">Ambalajı açılmamış ürünlerde 14 gün içinde koşulsuz iade.</p></div>
        </TabsContent>
        <TabsContent value="reviews" className="bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8 mt-4">
          <div className="space-y-5">
            {reviews.length === 0 && <p className="text-stone-500 text-sm">Henüz yorum yok. İlk yorumu siz yapın.</p>}
            {reviews.map((r) => (
              <div key={r.review_id} className="border-b border-stone-100 pb-4">
                <div className="flex items-center gap-2">
                  <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-gold text-gold" : "text-stone-300"}`} />)}</div>
                  <span className="font-semibold text-sm text-stone-800">{r.name}</span>
                </div>
                <p className="text-sm text-stone-600 mt-1.5">{r.comment}</p>
              </div>
            ))}
          </div>
          {user ? (
            <form onSubmit={submitReview} className="mt-8 bg-cream-dark/50 rounded-xl p-5">
              <h4 className="font-semibold text-stone-900">Yorum Yaz</h4>
              <div className="flex gap-1 mt-3">
                {[1,2,3,4,5].map((s) => (
                  <button type="button" key={s} data-testid={`review-star-${s}`} onClick={() => setRv({ ...rv, rating: s })}><Star className={`w-6 h-6 ${s <= rv.rating ? "fill-gold text-gold" : "text-stone-300"}`} /></button>
                ))}
              </div>
              <textarea data-testid="review-comment-input" value={rv.comment} onChange={(e) => setRv({ ...rv, comment: e.target.value })} required rows={3} placeholder="Deneyiminizi paylaşın..." className="w-full mt-3 px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
              <button data-testid="review-submit" className="mt-3 bg-forest hover:bg-forest-dark text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">Gönder</button>
            </form>
          ) : <p className="mt-6 text-sm text-stone-500">Yorum yazmak için <Link to="/giris" className="text-forest font-semibold">giriş yapın</Link>.</p>}
        </TabsContent>
        <TabsContent value="qa" className="bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8 mt-4">
          <div className="space-y-5">
            {qa.length === 0 && <p className="text-stone-500 text-sm">Henüz sorulmuş soru yok. İlk soruyu siz sorun.</p>}
            {qa.map((q) => (
              <div key={q.question_id} data-testid={`qa-item-${q.question_id}`} className="border-b border-stone-100 pb-4">
                <p className="text-sm font-semibold text-stone-800">{q.question}</p>
                <p className="text-[11px] text-stone-400 mt-0.5">{q.name}</p>
                <p className="text-sm text-stone-600 mt-2 bg-cream-dark/60 rounded-xl p-3"><span className="font-semibold text-forest">Yanıt:</span> {q.answer}</p>
              </div>
            ))}
          </div>
          {qaDone ? (
            <p data-testid="qa-success" className="mt-6 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">Sorunuz alındı — yanıtlandığında bu sayfada yayınlanacak.</p>
          ) : (
            <form onSubmit={submitQuestion} className="mt-8 bg-cream-dark/50 rounded-xl p-5 space-y-3">
              <h4 className="font-semibold text-stone-900">Soru Sor</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="qa-name" className="h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="Adınız *" value={qaForm.name} onChange={(e) => setQaForm({ ...qaForm, name: e.target.value })} required />
                <input data-testid="qa-email" type="email" className="h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="E-posta (opsiyonel)" value={qaForm.email} onChange={(e) => setQaForm({ ...qaForm, email: e.target.value })} />
              </div>
              <textarea data-testid="qa-question" value={qaForm.question} onChange={(e) => setQaForm({ ...qaForm, question: e.target.value })} required minLength={5} rows={3} placeholder="Ürün hakkında merak ettikleriniz..." className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
              <button data-testid="qa-submit" className="bg-forest hover:bg-forest-dark text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">Soruyu Gönder</button>
            </form>
          )}
        </TabsContent>
      </Tabs>

      {alsoBought.length > 0 && (
        <section data-testid="also-bought-section" className="mt-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-forest mb-2 flex items-center gap-3"><Users className="w-6 h-6 text-gold-dark" /> Bu Ürünü Alanlar Bunları da Aldı</h2>
          <p className="text-sm text-stone-500 mb-8">Diğer müşterilerimizin birlikte tercih ettiği ürünler</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {alsoBought.slice(0, 4).map((r) => <ProductCard key={r.product_id} product={r} />)}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-forest mb-8">Benzer Ürünler</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {related.slice(0, 4).map((r) => <ProductCard key={r.product_id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
